import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import { Box, CircularProgress, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { marked } from 'marked';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../markdown-table-custom.css';

// Style constants
const COLORS = {
  background: '#EEF5FF',
  border: '#E0E0E0',
  primary: '#1B54F8',
  icon: '#616161',
  scrollbarTrack: '#f1f1f1',
  scrollbarThumb: '#c1c1c1',
  scrollbarThumbHover: '#a1a1a1',
  backdrop: 'rgba(0,0,0,0.4)',
} as const;

const SCROLLBAR_STYLES = {
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: COLORS.scrollbarTrack,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: COLORS.scrollbarThumb,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: COLORS.scrollbarThumbHover,
  },
} as const;

const SCROLLBAR_STYLES_SMALL = {
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: COLORS.scrollbarTrack,
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: COLORS.scrollbarThumb,
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: COLORS.scrollbarThumbHover,
  },
} as const;

const MARKDOWN_CONTENT_STYLES = {
  whiteSpace: 'normal' as const,
  lineHeight: 2,
  wordWrap: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  maxWidth: '100%',
} as const;

interface EvaluationReportPanelProps {
  markdownContent?: string;
  onClose: () => void;
  loading: boolean;
  /** True when shown on AI Solution Blueprint screen */
  isAiSolutionBlueprint?: boolean;
  /** True when in minimized/sidebar view (not fullscreen) */
  isMinimized?: boolean;
  isFitToContent?: boolean;
  maxWidth?: string;
}

const EvaluationReportPanel: React.FC<EvaluationReportPanelProps> = ({
  markdownContent = '',
  onClose,
  loading,
  isAiSolutionBlueprint = false,
  isMinimized = true,
  isFitToContent = false,
  maxWidth = '400px',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobileQuery = useMediaQuery('(max-width:600px)');
  const [isMobileLocked] = useState(isMobileQuery);

  const parsedContent = useMemo(() => {
    const cleanedMarkdown = (markdownContent || '').replace(/\n\n\d+\.\s*/g, '\n\n\n ');
    return marked(cleanedMarkdown);
  }, [markdownContent]);

  const boxRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(300); // fallback height

  const updateHeight = useCallback(() => {
    if (boxRef.current && !isExpanded) {
      const offsetTop = boxRef.current.getBoundingClientRect().top;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const newHeight = isMobileLocked
        ? Math.max(240, Math.floor(viewportHeight * 0.6))
        : viewportHeight - offsetTop - 200;
      setHeight(newHeight);
    }
  }, [isExpanded, isMobileLocked]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    updateHeight();

    window.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, [updateHeight]);

  // Reusable components
  const LoadingSpinner = useMemo(
    () => (
      <Stack
        spacing={2}
        alignItems="center"
        sx={{
          maxWidth: '100%',
          width: 'fit-content',
        }}
      >
        <CircularProgress sx={{ flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontStyle: 'normal',
            color: COLORS.primary,
            fontSize: '14px',
            lineHeight: '100%',
            letterSpacing: 0,
            textAlign: 'center',
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          Loading recommendations
        </Typography>
      </Stack>
    ),
    []
  );

  const MarkdownContent = useMemo(
    () =>
      parsedContent ? (
        <div
          dangerouslySetInnerHTML={{ __html: parsedContent }}
          className="markdown-body-2"
          style={{
            ...MARKDOWN_CONTENT_STYLES,
            fontSize: '16px',
          }}
        />
      ) : (
        <Typography variant="body1" color="text.secondary">
          No content available.
        </Typography>
      ),
    [parsedContent]
  );

  const title = isAiSolutionBlueprint ? 'Evaluation Report' : 'Hackett Hubble Feedback';
  const description = isAiSolutionBlueprint
    ? 'Suggestions generated based on your inputs and evaluation results.'
    : 'Requirement Intake Consideration';

  // When expanded, show fullscreen view
  if (isExpanded) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '100%',
            }}
          >
            {title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleExpandToggle}
              sx={{ color: COLORS.icon, p: 1 }}
              aria-label="Exit Fullscreen"
            >
              <CloseFullscreenOutlinedIcon />
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: COLORS.icon, p: 1 }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '140%',
            p: 2,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          {description}
        </Typography>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
              }}
            >
              {LoadingSpinner}
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'white',
                p: 3,
                ...SCROLLBAR_STYLES,
              }}
            >
              {MarkdownContent}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Mobile bottom sheet view (non-expanded)
  if (isMobileLocked) {
    const MobileLoadingSpinner = (
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: '100%', width: 'fit-content' }}>
        <CircularProgress sx={{ flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: COLORS.primary,
            fontSize: '12px',
            lineHeight: '100%',
            textAlign: 'center',
          }}
        >
          Loading recommendations
        </Typography>
      </Stack>
    );

    const MobileMarkdownContent = parsedContent ? (
      <div
        dangerouslySetInnerHTML={{ __html: parsedContent }}
        className="markdown-body-2"
        style={MARKDOWN_CONTENT_STYLES}
      />
    ) : (
      <Typography variant="body2" color="text.secondary">
        No content available.
      </Typography>
    );

    return (
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300 }}>
        {/* Backdrop */}
        <Box
          onClick={onClose}
          sx={{ position: 'absolute', inset: 0, backgroundColor: COLORS.backdrop }}
        />
        {/* Sheet */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '72vh',
            backgroundColor: 'white',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.2)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: COLORS.background,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '100%',
              }}
            >
              {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleExpandToggle}
                sx={{ color: COLORS.icon, p: 1 }}
                aria-label="Expand Fullscreen"
              >
                <FullscreenIcon />
              </IconButton>
              <IconButton onClick={onClose} sx={{ color: COLORS.icon, p: 1 }} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {/* Description */}
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              padding: '8px 16px',
              backgroundColor: COLORS.background,
            }}
          >
            {description}
          </Typography>
          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              backgroundColor: COLORS.background,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white',
                }}
              >
                {MobileLoadingSpinner}
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2 }}>
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    p: 2,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  }}
                >
                  {MobileMarkdownContent}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Normal sidebar view
  const SidebarLoadingSpinner = (
    <Stack
      spacing={2}
      alignItems="center"
      sx={{
        maxWidth: '100%',
        width: 'fit-content',
      }}
    >
      <CircularProgress sx={{ flexShrink: 0 }} />
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontStyle: 'normal',
          color: COLORS.primary,
          fontSize: '12px',
          lineHeight: '100%',
          letterSpacing: 0,
          textAlign: 'center',
          wordBreak: 'break-word',
          width: '100%',
          margin: '0 auto',
        }}
      >
        Loading recommendations
      </Typography>
    </Stack>
  );

  const SidebarMarkdownContent = parsedContent ? (
    <div
      dangerouslySetInnerHTML={{ __html: parsedContent }}
      className="markdown-body-2"
      style={{
        ...MARKDOWN_CONTENT_STYLES,
        paddingTop: 16,
        paddingLeft: 16,
      }}
    />
  ) : (
    <Typography variant="body2" color="text.secondary">
      No content available.
    </Typography>
  );

  return (
    <Box
      sx={{
        backgroundColor: COLORS.background,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        padding: 2,
        pr: 0,
        width: '100%',
        maxWidth: maxWidth,
        position: 'sticky',
        top: isMinimized ? 0 : 68,
        scrollbarGutter: 'stable',
        alignSelf: 'flex-start',
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        opacity: 1,
        flexShrink: 0,
        maxHeight: isFitToContent ? '63vh' : isMinimized ? '58vh' : 'calc(100vh - 30px)',
        ...((isMinimized || isFitToContent) && { height: '100%' }),
      }}
    >
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            {title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              onClick={handleExpandToggle}
              sx={{ color: COLORS.icon, p: 0 }}
              aria-label="Expand"
              size="small"
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{ color: COLORS.icon, p: 0 }}
              aria-label="Close"
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          ref={boxRef}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '140%',
            mb: 2,
          }}
        >
          {description}
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            backgroundColor: 'white',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            p: 2,
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            boxSizing: 'border-box',
            width: '100%',
          }}
        >
          {SidebarLoadingSpinner}
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'white',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            p: 1,
            height: `${height}px`,
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            width: '100%',
            ...SCROLLBAR_STYLES_SMALL,
          }}
        >
          {SidebarMarkdownContent}
        </Box>
      )}
    </Box>
  );
};

export default EvaluationReportPanel;
